import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MENTORUM_DUYGU_ETIKETI,
  mentorumDisaAktarSemasi,
  etkinlikYanitiKopyasiSemasi,
  type MentorumDuygu,
} from "@koclukpanel/contracts";
import { Buton, Kart } from "@koclukpanel/ui";
import { api, ApiHatasi } from "./api";
import { Hata403 } from "./SistemSayfalari";
import { izinSorgusu, type IzinHaritasi } from "./izin";
import {
  MENTORUM_OKUMA,
  MentorumForm,
  MentorumHata,
  MentorumOgrenciSec,
  mentorumTarih,
} from "./MentorumYonetim";

interface Kayitlar {
  duygular: { id: string; mood: MentorumDuygu; day: string; createdAt: string }[];
  yanitlar: {
    id: string;
    status: string;
    submittedAt: string;
    snapshot: unknown;
    textAnswer: string | null;
    selectedOptionIds: string[];
  }[];
}

export function MentorumKayitlar({
  izinler,
  ilkOgrenciId,
}: {
  izinler: IzinHaritasi;
  ilkOgrenciId?: string;
}) {
  const izinVar = izinSorgusu(izinler);
  const [ogrenciId, setOgrenciId] = useState(ilkOgrenciId ?? "");
  const izinli = izinVar("mentorum.kayit.goruntule");
  const kayitlar = useQuery({
    queryKey: ["mentorum", "kayitlar", ogrenciId],
    queryFn: ({ signal }) =>
      api<Kayitlar>(`/mentorum/kayitlar/${encodeURIComponent(ogrenciId)}`, { signal }),
    enabled: izinli && !!ogrenciId,
    ...MENTORUM_OKUMA,
  });
  if (!izinli) return <Hata403 onBugun={() => window.location.assign("/")} />;
  if (!ogrenciId) return <MentorumOgrenciSec izinler={izinler} onSec={setOgrenciId} />;
  // F-MNT-12/13 — yalnız okuma; her açılış günlüklenen uca gider, ret halinde eski içerik çizilmez.
  return (
    <div className="flex min-w-0 flex-col gap-4 break-words">
      <h2 className="text-notr-900 text-xl font-semibold">Mentörüm kayıtları</h2>
      <p>Kayıtlar salt okunur. Bu görüntüleme erişim günlüğüne yazılır.</p>
      <Buton gorunum="ikincil" onClick={() => setOgrenciId("")}>
        Öğrenci değiştir
      </Buton>
      {kayitlar.isPending ? (
        <p role="status">Kayıtlar yükleniyor…</p>
      ) : kayitlar.isError ? (
        <MentorumHata hata={kayitlar.error} />
      ) : (
        <>
          <Kart baslik="Duygu kayıtları">
            {kayitlar.data.duygular.length ? (
              <ul className="space-y-3">
                {kayitlar.data.duygular.map((duygu) => (
                  <li key={duygu.id}>
                    {new Date(duygu.day).toLocaleDateString("tr-TR", {
                      timeZone: "Europe/Istanbul",
                    })}{" "}
                    · {MENTORUM_DUYGU_ETIKETI[duygu.mood]}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Duygu kaydı bulunmuyor.</p>
            )}
          </Kart>
          <Kart baslik="Gönderilmiş etkinlik yanıtları">
            <div className="flex min-w-0 flex-col gap-4">
              {kayitlar.data.yanitlar.length ? (
                kayitlar.data.yanitlar.map((yanit) => {
                  const kopya = etkinlikYanitiKopyasiSemasi.safeParse(yanit.snapshot);
                  return (
                    <article
                      key={yanit.id}
                      className="border-notr-200 min-w-0 space-y-2 border-b pb-4"
                    >
                      <p>{mentorumTarih(yanit.submittedAt)}</p>
                      {kopya.success ? (
                        <>
                          <h3 className="font-semibold">{kopya.data.prompt}</h3>
                          <ul>
                            {kopya.data.options
                              .filter((secenek) => secenek.selected)
                              .map((secenek) => (
                                <li key={secenek.id}>{secenek.text}</li>
                              ))}
                          </ul>
                          {kopya.data.textPrompt && <p>{kopya.data.textPrompt}</p>}
                        </>
                      ) : (
                        <p>Kayıt kopyası görüntülenemiyor.</p>
                      )}
                      {yanit.textAnswer && (
                        <p className="break-words whitespace-pre-wrap">{yanit.textAnswer}</p>
                      )}
                    </article>
                  );
                })
              ) : (
                <p>Gönderilmiş yanıt bulunmuyor.</p>
              )}
            </div>
          </Kart>
          {izinVar("mentorum.kayit.disaaktar") && (
            <DisaAktar key={ogrenciId} ogrenciId={ogrenciId} />
          )}
        </>
      )}
    </div>
  );
}

function DisaAktar({ ogrenciId }: { ogrenciId: string }) {
  return (
    <MentorumForm
      baslik="Resmi başvuru kopyası"
      dugme="Kayıtları dışa aktar"
      alanlar={[{ ad: "reason", etiket: "Resmi başvuru gerekçesi", tur: "textarea" }]}
      gonder={async (deger) => {
        const sonuc = mentorumDisaAktarSemasi.safeParse({
          studentId: ogrenciId,
          reason: deger.reason,
        });
        if (!sonuc.success)
          throw new ApiHatasi(
            400,
            "DOGRULAMA",
            sonuc.error.issues[0]?.message ?? "Resmi başvuru gerekçesi gereklidir.",
          );
        // F-MNT-16/K-25 — kopya yalnız sudo korumalı uçtan üretilir; ekrandaki veriden kopya hazırlanmaz.
        const veri = await api<Kayitlar & { studentId: string }>("/mentorum/disa-aktar", {
          yontem: "POST",
          govde: sonuc.data,
        });
        const baglanti = URL.createObjectURL(
          new Blob([JSON.stringify(veri, null, 2)], { type: "application/json" }),
        );
        const indirme = document.createElement("a");
        indirme.href = baglanti;
        indirme.download = "mentorum-kayitlari.json";
        indirme.click();
        setTimeout(() => URL.revokeObjectURL(baglanti), 0);
      }}
    />
  );
}
